import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { IntegrationConnectionResultMapper } from '../../mappers/IntegrationConnectionResultMapper';
import { IntegrationConnectionResult } from '../../models/IntegrationConnectionResult';
import { VenafiMapper } from '../mappers/VenafiMapper';
import { Venafi } from '../models/Venafi';

export const POPULATE_VENAFI_GUIDS_QUERY = `
mutation($reportId: ID!){
  populateVenafiGuids(input: { reportId: $reportId }) {
    clientMutationId
  }
}
`;

export const VENAFI_CERTIFICATE_DETAILS_QUERY = `
mutation($certificateId: ID!) {
  getVenafiCertificateDetails(input: { certificateId: $certificateId }) {
    details
  }
}
`;

export const VENAFI_UPLOAD_CERTIFICATE_QUERY = `
mutation($certificateId: ID!, $policyDn: String!, $reconcile: Boolean!) {
  uploadCertificateToVenafi(input: { certificateId: $certificateId, policyDn: $policyDn, reconcile: $reconcile }) {
    guid
  }
}
`;

export const UPDATE_VENAFI_INTEGRATION = `
mutation($url: String!, $accessToken: String!, $trustedCaCertificate: String) {
  updateVenafiIntegration(input: { url: $url, accessToken: $accessToken, trustedCaCertificate: $trustedCaCertificate }) {
    venafiIntegration {
      url
      accessToken
      trustedCaCertificate
    }
  }
}
`;

export const CHECK_VENAFI_INTEGRATION = `
mutation($url: String!, $accessToken: String!, $trustedCaCertificate: String) {
  checkVenafiIntegration(input: { url: $url, accessToken: $accessToken, trustedCaCertificate: $trustedCaCertificate }) {
    venafiConnectionResult {
      isSuccessful
      reason
    }
  }
}
`;

export const UNLINK_VENAFI_INTEGRATION = `
mutation {
  unlinkVenafiIntegration(input: {}) {
    __typename
  }
}
`;

export type UpdateVenafiIntegrationArgs = {
  url: string;
  accessToken: string;
  trustedCaCertificate: string;
};

export type CheckVenafiIntegrationArgs = UpdateVenafiIntegrationArgs;

export class VenafiRepository extends BaseGQLService {
  private readonly connectionResultMapper = new IntegrationConnectionResultMapper();
  private readonly venafiMapper = new VenafiMapper();

  async updateVenafiForReport(reportId: number): Promise<void> {
    return this.mutate(POPULATE_VENAFI_GUIDS_QUERY, {
      reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
    });
  }

  async getCertificateDetails(certificateId: number): Promise<object | null> {
    return this.mutate(
      VENAFI_CERTIFICATE_DETAILS_QUERY,
      {
        certificateId: this.gqlIdEncoder.encode(ModelType.Certificate, certificateId),
      },
      (data) => {
        try {
          return JSON.parse(data.getVenafiCertificateDetails.details);
        } catch {
          return null;
        }
      }
    );
  }

  async uploadCertificate(certificateId: number, policy: string): Promise<string> {
    return this.mutate(
      VENAFI_UPLOAD_CERTIFICATE_QUERY,
      {
        certificateId: this.gqlIdEncoder.encode(ModelType.Certificate, certificateId),
        policyDn: policy,
        reconcile: false,
      },
      (data) => data.uploadCertificateToVenafi.guid
    );
  }

  async checkIntegrationConnection(args: CheckVenafiIntegrationArgs): Promise<IntegrationConnectionResult> {
    return this.mutate(CHECK_VENAFI_INTEGRATION, args, (data) => this.connectionResultMapper.mapGQLModel(data.checkVenafiIntegration.venafiConnectionResult));
  }

  async updateIntegration(args: UpdateVenafiIntegrationArgs): Promise<Venafi> {
    return this.mutate(UPDATE_VENAFI_INTEGRATION, args, (data) => this.venafiMapper.mapGQLModel(data.updateVenafiIntegration.venafiIntegration));
  }

  async unlinkIntegration(): Promise<void> {
    return this.mutate(UNLINK_VENAFI_INTEGRATION, {});
  }
}
